import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box } from '@mui/system'
import { Checkbox, DatePicker, Select } from 'src/Libs/inputs/formik'
import TextField from 'src/Libs/inputs/formik/TextField'
import { makeStyles } from 'tss-react/mui'
import { Moment } from '@e3dc-react/shell/Libs/moment'
import { fetchCurrentAndNextYearHolidayDates, isDateWeekend } from 'src/Libs/shop/utils'
import { isSameDay } from '@e3dc-react/shell/Libs/date'
import moment from 'moment'
import { Grid, Typography } from '@mui/material'
import { generateSmartMeterTimeOptions } from 'src/Libs/shop/utils/smart-meter'
import { DayOfWeek } from 'src/Libs/shop/types'

export interface SmartMeterFormValues {
  firstName: string
  lastName: string
  street: string
  streetNumber: string
  zipCode: string
  city: string
  email: string
  mechanicName: string
  previousMeterNumber: string
  targetInstallationDate: string
  targetInstallationTime: string
  administrationNumber: string | undefined
  confirmCustomerData: boolean
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: theme.spacing(2),
    maxWidth: '450px',
    marginTop: theme.spacing(2),
  },
  fullWidthField: {
    flex: '1 1 100%',
  },
  threeQuarterWidthField: {
    flex: `1 1 calc(70% - ${theme.spacing(1)})`,
    minWidth: '300px',
  },
  halfWidthField: {
    flex: `1 1 calc(50% - ${theme.spacing(1)})`,
    minWidth: '200px',
  },
  quarterWidthField: {
    flex: `1 1 calc(30% - ${theme.spacing(1)})`,
    minWidth: '80px',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkBoxContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  label: {
    width: '100%',
    marginTop: theme.spacing(4),
  },
  customerLabel: {
    width: '100%',
  },
}))

/*
 * This component renders the input field form of smartmeters
 */
const SmartMeterForm: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const availableHolidays = fetchCurrentAndNextYearHolidayDates()
  /* 6 week + 1 day since the smart meter order can happend only after 6 weeks */
  const sixWeeksFromCurrentDate = moment().startOf('day').add(6, 'weeks').add(1, 'day')

  const isDateDisabled = (date: Moment): boolean => {
    // Convert Moment from DatePicker to Date
    const dateAsDate = date.toDate()

    if (isDateWeekend(dateAsDate)) {
      return true
    }

    if (dateAsDate.getDay() === DayOfWeek.Monday) {
      return true
    }

    if (availableHolidays.some(holiday => isSameDay(holiday, dateAsDate))) {
      return true
    }
    return false
  }

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.container}>
        <Typography className={classes.customerLabel} variant="h3">
          {t('shop.smart-meter-document.customer-label')}
        </Typography>
        <TextField className={classes.halfWidthField} required name={'firstName'} label={t('shop.smart-meter-document.first-name-label')} />
        <TextField className={classes.halfWidthField} required name={'lastName'} label={t('shop.smart-meter-document.last-name-label')} />
        <TextField className={classes.threeQuarterWidthField} required name={'street'} label={t('shop.smart-meter-document.street-label')} />
        <TextField className={classes.quarterWidthField} required name={'streetNumber'} label={t('shop.smart-meter-document.street-number-label')} />
        <TextField className={classes.quarterWidthField} required name={'zipCode'} label={t('shop.smart-meter-document.zip-code-label')} />
        <TextField className={classes.threeQuarterWidthField} required name={'city'} label={t('shop.smart-meter-document.city-label')} />
        <TextField className={classes.fullWidthField} required name={'email'} label={t('shop.smart-meter-document.email-label')} />
        <Typography variant="h3" className={classes.label}>
          {t('shop.smart-meter-document.installer-label')}
        </Typography>
        <TextField className={classes.halfWidthField} required name={'mechanicName'} label={t('shop.smart-meter-document.mechanicName-label')} />
        <TextField
          className={classes.halfWidthField}
          required
          name={'previousMeterNumber'}
          label={t('shop.smart-meter-document.previousMeterNumber-label')}
        />
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <DatePicker
              className={classes.halfWidthField}
              required
              minDate={sixWeeksFromCurrentDate}
              shouldDisableDate={isDateDisabled}
              name={'targetInstallationDate'}
              label={t('shop.smart-meter-document.targetInstallationDate-label')}
            />
          </Grid>
          <Grid item xs={6}>
            <Select
              className={classes.halfWidthField}
              required
              name={'targetInstallationTime'}
              label={t('shop.smart-meter-document.targetInstallationTimelabel')}
              options={generateSmartMeterTimeOptions()}
            />
          </Grid>
        </Grid>
        <TextField
          className={classes.halfWidthField}
          name={'administrationNumber'}
          label={t('shop.smart-meter-document.administration-number-label')}
        />
        <Box className={classes.checkBoxContainer}>
          <Checkbox name={'confirmCustomerData'} label={t('shop.smart-meter-document.accept-data-label')} />
        </Box>
      </Box>
    </Box>
  )
}

export default SmartMeterForm
